import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, FileText, Target, Map, BarChart3 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Artifact {
  id: string;
  type: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const SavedArtifactsPage = () => {
  console.log("Rendering SavedArtifactsPage");
  const navigate = useNavigate();

  const { data: artifacts, isLoading } = useQuery({
    queryKey: ['artifacts'],
    queryFn: async () => {
      console.log("Fetching artifacts");
      const { data, error } = await supabase
        .from('artifacts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching artifacts:", error);
        toast.error("Failed to load artifacts");
        throw error;
      }

      console.log("Fetched artifacts:", data);
      return data as Artifact[];
    },
  });

  const getArtifactIcon = (type: string) => {
    switch (type) {
      case 'big-sheet':
        return FileText;
      case 'okr':
        return Target;
      case 'power-move':
        return Map;
      case 'opportunity':
        return BarChart3;
      default:
        return FileText;
    }
  };

  const getArtifactPath = (type: string) => {
    switch (type) {
      case 'big-sheet':
        return '/artifacts/big-sheet';
      case 'okr':
        return '/artifacts/okrs';
      case 'power-move':
        return '/artifacts/power-moves';
      case 'opportunity':
        return '/artifacts/opportunity-audit';
      default:
        return '/artifacts';
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="container max-w-7xl mx-auto px-4">
        {/* Header Section */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/artifacts")}
            className="hover:bg-accent"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-text-primary">Saved Artifacts</h1>
            <p className="text-text-secondary mt-1">View and manage your career artifacts</p>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-text-secondary">Loading your artifacts...</p>
          </div>
        ) : !artifacts?.length ? (
          <Card className="p-6 text-center">
            <h3 className="text-lg font-medium mb-2">No Artifacts Yet</h3>
            <p className="text-text-secondary mb-6">
              Start building your career portfolio by creating your first artifact
            </p>
            <Button onClick={() => navigate("/artifacts")}>
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Artifact
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {artifacts.map((artifact) => {
              const Icon = getArtifactIcon(artifact.type);
              return (
                <Card
                  key={artifact.id}
                  className="p-6 hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => navigate(getArtifactPath(artifact.type))}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-1">{artifact.name}</h3>
                      <p className="text-sm text-text-secondary mb-2">
                        {artifact.type.split('-').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 rounded-full bg-secondary/10 text-secondary">
                          {artifact.status}
                        </span>
                        <span className="text-xs text-text-secondary">
                          Last updated: {new Date(artifact.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedArtifactsPage;